import { Reminder } from '../types';

const convertVapidKey = (base64String: string) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
}

export async function subscribeToPushNotifications() {
  try {
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
      if (!vapidPublicKey) {
        throw new Error('VAPID public key not found');
      }

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertVapidKey(vapidPublicKey)
      });

      // Store the subscription on your server here
      console.log('Push Subscription:', subscription);
    }

    return subscription;
  } catch (error) {
    console.error('Error subscribing to push notifications:', error);
    return null;
  }
}

export async function scheduleNotification(reminder: Reminder) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.log('Push notifications not supported');
    return;
  }

  const permission = await requestNotificationPermission();
  if (!permission) {
    console.log('Notification permission denied');
    return;
  }

  const subscription = await subscribeToPushNotifications();
  if (!subscription) {
    console.log('Failed to get push subscription');
    return;
  }

  // Calculate notification time
  const dueDate = new Date(reminder.dueDate);
  const notifyDate = new Date(dueDate);
  notifyDate.setDate(dueDate.getDate() - (reminder.notifyBefore || 1));

  // Store reminder in local storage
  const reminders = JSON.parse(localStorage.getItem('reminders') || '[]');
  reminders.push(reminder);
  localStorage.setItem('reminders', JSON.stringify(reminders));

  // For testing purposes (30 seconds)
  if (reminder.notifyBefore === 0) {
    // Send push notification immediately for testing
    await sendPushNotification(subscription, {
      title: `Test Notification: ${reminder.title}`,
      body: `Amount: $${reminder.amount}`,
      id: reminder.id,
      url: '/?reminder=' + reminder.id
    });
    return;
  }

  // Schedule the actual notification
  const timeUntilNotification = notifyDate.getTime() - Date.now();
  if (timeUntilNotification > 0) {
    setTimeout(async () => {
      await sendPushNotification(subscription, {
        title: `Bill Reminder: ${reminder.title}`,
        body: `Your ${reminder.title} bill of $${reminder.amount} is due in ${reminder.notifyBefore || 1} day(s)`,
        id: reminder.id,
        url: '/?reminder=' + reminder.id
      });
    }, timeUntilNotification);
  }
}

async function sendPushNotification(
  subscription: PushSubscription,
  data: { title: string; body: string; id: string; url: string }
) {
  try {
    // In a production environment, this should be sent to your server
    // which would then use web-push to send the notification
    // For demo purposes, we're using the service worker directly
    const registration = await navigator.serviceWorker.ready;
    registration.active?.postMessage({
      type: 'SCHEDULE_NOTIFICATION',
      payload: {
        title: data.title,
        body: data.body,
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-96x96.png',
        vibrate: [100, 50, 100],
        tag: data.id,
        renotify: true,
        requireInteraction: true,
        data: {
          url: data.url,
          id: data.id
        },
        actions: [
          {
            action: 'view',
            title: 'View Details'
          },
          {
            action: 'dismiss',
            title: 'Dismiss'
          }
        ]
      }
    });
  } catch (error) {
    console.error('Error sending push notification:', error);
  }
}

export function checkForDueReminders() {
  const reminders: Reminder[] = JSON.parse(localStorage.getItem('reminders') || '[]');
  const now = new Date();

  reminders.forEach(reminder => {
    const dueDate = new Date(reminder.dueDate);
    const notifyDate = new Date(dueDate);
    notifyDate.setDate(dueDate.getDate() - (reminder.notifyBefore || 1));

    if (notifyDate <= now) {
      scheduleNotification(reminder);
    }
  });
}